<template>
  <div
    v-if="contentBanner"
    ref="contentBlock"
    class="content-banner__block"
    :class="{
      [$class('active')]: getComponentVisible('ContentBanner')
    }"
    :style="styles"
    data-component-animation
  >
    <div class="content-banner__content">
      <p
        v-if="contentBanner?.bannerTitle"
        class="content-banner__title"
        :class="titleClasses"
        v-text="contentBanner?.bannerTitle"
      />

      <a
        v-if="contentBanner?.bannerLink && contentBanner?.bannerLinkText"
        class="
          button
          button--text
          content-banner__button
          text-button-s
        "
        :href="contentBanner.bannerLink"
        v-text="contentBanner.bannerLinkText"
      />
    </div>

    <a
      v-if="contentBanner?.image"
      :href="contentBanner.bannerLink"
      class="content-banner__image"
    >
      <art-direction
        :image="contentBanner.image"
        :image-desktop="contentBanner.imageDesktop"
        ratio="16:9"
        :alt="getImageAlt()"
      />
    </a>
  </div>
</template>

<script>

/**
 * Vue: Content banner (content-banner)
 * -----------------------------------------------------------------------------
 * Users will see a banner within the Collection Page/PLP redirecting them
 * toward a content-based page.
 *
 * @param {Object} contentBanner - Content for in-grid-content-banner.
 *
 */
import ArtDirection from '~global/images/art-direction'


export default {
  name: 'ContentBanner',

  components: {
    ArtDirection,
  },

  props: {
    contentBanner: {
      type: Object,
      default: () => {},
    },
    plpColumnCount: {
      type: [Number, String],
      default: 4,
    },
  },

  data() {
    return {
      verticalAlignmentMapping: {
        'bottom': 'flex-end',
        'center': 'center',
        'top': 'flex-start',
      },
      horizontalAlignmentMapping: {
        'left': 'flex-start',
        'center': 'center',
        'right': 'flex-end',
      },
    }
  },

  computed: {

    /**
     * Compute animation delay.
     * @returns {String}
     */
    animationDelay() {
      return this.$timing
        ('quick', { index: this.index, unit: 's' })
    },

    styles() {
      const { column_span: columnSpan = 1, row_span: rowSpan = 1 } = this.contentBanner?.[`${this.plpColumnCount}-grid-layout`] || {}

      const styles = {
        '--column-span': columnSpan,
        '--row-span': rowSpan,
      }

      const { textColor, overlayOpacity, horizontalAlignment, verticalAlignment } = this.contentBanner

      if (textColor) {
        styles['--text-color'] = textColor
      }

      if (overlayOpacity) {
        styles['--overlay-opacity'] = overlayOpacity
      }

      if (horizontalAlignment) {
        styles['--horizontal-alignment'] = this.horizontalAlignmentMapping[horizontalAlignment]
        styles.textAlign = horizontalAlignment
      }

      if (verticalAlignment) {
        styles['--vertical-alignment'] = this.verticalAlignmentMapping[verticalAlignment]
      }

      return styles
    },

    /**
     * Compute classes for the title text
     * @returns {String} - classes string
     */
    titleClasses() {
      if (this.contentBanner.textSize === 'large') {
        return 'content-banner__title--large text-body-s text-heading-l-desktop'
      }

      return 'text-heading-s text-heading-l-desktop'
    },
  },

  methods: {

    /**
     * Returns a suitable alt string for the banner image.
     * @returns {String}
     */
    getImageAlt() {
      return this.contentBanner.image.alt ||
             this.contentBanner.imageDesktop.alt ||
             this.contentBanner.bannerTitle
    },
  },
}
</script>

<style lang="scss">
@import './content-banner';
</style>
